import React, { useCallback, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faLaptop, faBell, faBatteryQuarter, faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons'
import BizPlusPage from '../../components/biz_plus/biz_plus'
import { useAppSelector } from '../../hooks'
import {
  getStaleDetectionWarningMessage,
  shouldShowStaleDetectionWarning,
} from '../../helpers/staleDetectionWarning'
import { getFromNowAsText } from '../../helpers/timeHelper'
import './DashboardDemo.css'

type DashboardFilter = 'all' | 'missing' | 'battery' | 'away'

const hasLowBattery = (mamorio: IMamorio) => (
  mamorio.estimated_remain_battery !== null
  && mamorio.estimated_remain_battery < 10
)

const DashboardDemoPage = () => {
  const [filter, setFilter] = useState<DashboardFilter>('all')
  const mamorios = useAppSelector((state) => state.mamorio.mamorios) ?? []
  const group = useAppSelector((state) => state.team.groups?.[0])
  const hasWarning = useCallback((mamorio: IMamorio) => (
    shouldShowStaleDetectionWarning(mamorio.recent_log?.at, group)
  ), [group])
  const filterMamorio = useCallback((mamorio: IMamorio) => (
    filter === 'all'
    || (filter === 'missing' && hasWarning(mamorio))
    || (filter === 'battery' && hasLowBattery(mamorio))
    || (filter === 'away' && mamorio.is_rent)
  ), [filter, hasWarning])
  const filters: {
    id: DashboardFilter
    title: string
    icon: typeof faLaptop
    count: number
    note: string
  }[] = [
    {
      id: 'all', title: '総デバイス', icon: faLaptop, count: mamorios.length, note: '登録済みのすべてのデバイス',
    },
    {
      id: 'missing', title: '未検知警告中', icon: faBell, count: mamorios.filter(hasWarning).length, note: 'グループの警告設定に基づく',
    },
    {
      id: 'battery', title: '電池切れ・残りわずか', icon: faBatteryQuarter, count: mamorios.filter(hasLowBattery).length, note: '推定電池残量が10%未満',
    },
    {
      id: 'away', title: '持出中', icon: faSignOutAlt, count: mamorios.filter((mamorio) => mamorio.is_rent).length, note: '持出中のデバイスを確認',
    },
  ]
  const alerts = mamorios.filter((mamorio) => (
    hasWarning(mamorio) || hasLowBattery(mamorio)
  ))

  return (
    <BizPlusPage
      collapsibleFilters
      filterMamorio={filterMamorio}
      beforeMap={(
        <section className="demo-kpis" aria-label="デバイスの状態で絞り込み">
          {filters.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`demo-kpi demo-kpi--${item.id}`}
              aria-pressed={filter === item.id}
              onClick={() => setFilter(item.id)}
            >
              <span className="demo-kpi-icon"><FontAwesomeIcon icon={item.icon} /></span>
              <span>
                <span className="demo-kpi-title">{item.title}</span>
                <span className="demo-kpi-value">
                  {item.count}
                  <small>台</small>
                </span>
                <span className="demo-kpi-note">{item.note}</span>
              </span>
            </button>
          ))}
        </section>
      )}
      renderMapAside={(focusMamorio) => (
        <section className="demo-alerts demo-panel" aria-label="要対応アラート">
          <header className="demo-panel-heading">
            <h2>
              <FontAwesomeIcon icon={faBell} />
              {' '}
              要対応アラート
            </h2>
            <span>
              {alerts.length}
              件
            </span>
          </header>
          {alerts.length === 0 ? (
            <p className="demo-alert-empty">現在、対応が必要なアラートはありません。</p>
          ) : (
            <ul>
              {alerts.map((mamorio) => (
                <li key={mamorio.id}>
                  <span className={`demo-alert-dot ${hasWarning(mamorio) ? 'demo-alert-dot--danger' : 'demo-alert-dot--warning'}`} />
                  <div>
                    <div className="demo-alert-title">
                      <strong>{mamorio.name}</strong>
                      <small>{mamorio.recent_log ? getFromNowAsText(mamorio.recent_log.at) : '未検知'}</small>
                    </div>
                    {hasWarning(mamorio) && <p>{getStaleDetectionWarningMessage(group)}</p>}
                    {hasLowBattery(mamorio) && (
                      <p>
                        推定電池残量
                        {mamorio.estimated_remain_battery}
                        %・電池の交換を推奨
                      </p>
                    )}
                    <button
                      type="button"
                      className="demo-action"
                      disabled={!mamorio.recent_log}
                      onClick={() => {
                        setFilter('all')
                        focusMamorio(mamorio)
                      }}
                    >
                      マップで確認 →
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    />
  )
}

export default DashboardDemoPage
